import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, ImageIcon } from "lucide-react";
import { uploadTownhallDeityImage, resolveTownhallImageSrc, townhallUploadedAssetSrc } from "@/lib/townhall/townhall-service";

const MAX_DIM = 512;

function resizeImage(file, maxDim) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const r = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * r);
        height = Math.round(height * r);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      const mime = file.type === "image/png" ? "image/png" : "image/jpeg";
      resolve(canvas.toDataURL(mime, 0.88));
    };
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Edit Picture — upload a new deity image for Townhall (stored in Sacred KV asset bucket).
 */
export function TownhallImageUpload({ chantId, imageUrl, onUploaded }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(resolveTownhallImageSrc(imageUrl));

  useEffect(() => {
    setPreview(resolveTownhallImageSrc(imageUrl));
  }, [imageUrl]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      let dataUrl;
      if (file.type === "image/svg+xml") {
        const text = await file.text();
        dataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(text)))}`;
      } else {
        dataUrl = await resizeImage(file, MAX_DIM);
      }
      setPreview(dataUrl);
      const res = await uploadTownhallDeityImage(chantId, dataUrl, file.type || "image/png");
      const nextUrl =
        res.display_url ||
        resolveTownhallImageSrc(res.image_url) ||
        resolveTownhallImageSrc(townhallUploadedAssetSrc(chantId)) ||
        dataUrl;
      setPreview(nextUrl);
      onUploaded?.(res.image_url || nextUrl, res.detail);
    } catch (err) {
      console.error(err);
      alert(`Upload failed — ${err.message || "Check admin/editor sign-in."}`);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-3 border rounded-lg p-4 bg-muted/20" data-testid="townhall-image-upload">
      <div className="flex justify-center">
        {preview ? (
          <img
            src={preview}
            alt="Deity preview"
            className="w-32 h-32 rounded-full object-cover border-2 border-amber-200"
            onError={() => setPreview(null)}
          />
        ) : (
          <div className="w-32 h-32 rounded-full bg-amber-50 flex items-center justify-center border border-dashed border-amber-300">
            <ImageIcon className="w-8 h-8 text-amber-400" />
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
        className="hidden"
        onChange={onFile}
        data-testid="townhall-image-file-input"
      />
      <Button
        type="button"
        variant="secondary"
        className="w-full"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid="edit-picture-btn"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
        Edit Picture
      </Button>
      <p className="text-[10px] text-muted-foreground text-center">
        PNG, JPG, WEBP, or SVG — stored securely and updates Townhall automatically
      </p>
    </div>
  );
}
